import { getSessionGeneration } from './sessionGeneration.js'
// Exact backend snapshot contract; no fallback to counts, timestamps or sheet data.
export function rosterSnapshot(map) {
  const s = map?.rosterSnapshot
  if (map?.ok !== true || s?.version !== 1 || !/^[a-f0-9]{64}$/.test(s?.fingerprint || '') || typeof s.present !== 'boolean'
    || (s.present ? typeof s.raw !== 'string' || typeof s.updatedAt !== 'string' || !s.document || typeof s.document !== 'object' || Array.isArray(s.document) || !s.document.bodies || typeof s.document.bodies !== 'object' || Array.isArray(s.document.bodies)
      : s.raw !== null || s.updatedAt !== null || s.document !== null)) {
    throw Object.assign(new Error('The current assignment version is unavailable. Reload the map; saving stays blocked until the paired backend is available.'),
      { status: 428, body: { ok: false, error: 'roster_snapshot_unavailable' }, rosterOutcome: 'review' })
  }
  if (s.present) {
    let document
    try { document = JSON.parse(s.raw) } catch { /* rejected below */ }
    if (JSON.stringify(document) !== JSON.stringify(s.document)) throw Object.assign(Error('The assignment snapshot is incomplete. Reload the map before saving.'), { status: 428, rosterOutcome: 'review' })
  }
  return s
}

export const ROSTER_PENDING_KEY = 'sr_roster_write_pending_v1'
const listeners = new Set()
const unavailable = JSON.stringify({ state: 'unavailable' })
export function rosterPendingText() {
  try { return globalThis.localStorage.getItem(ROSTER_PENDING_KEY) || '' } catch { return unavailable }
}
export function rosterPending() {
  try { return JSON.parse(rosterPendingText() || 'null') } catch { return { state: 'unavailable' } }
}
const notify = () => { for (const listener of listeners) listener() }
export function subscribeRosterPending(listener) {
  listeners.add(listener)
  globalThis.window?.addEventListener('storage', listener)
  return () => { listeners.delete(listener); globalThis.window?.removeEventListener('storage', listener) }
}
export function savePending(value) {
  globalThis.localStorage.setItem(ROSTER_PENDING_KEY, JSON.stringify(value))
  if (rosterPending()?.id !== value.id) throw Error('Could not retain the pending assignment change')
  notify()
}
export function clearPending(id) {
  if (rosterPending()?.id !== id) return
  globalThis.localStorage.removeItem(ROSTER_PENDING_KEY)
  notify()
}
export function rosterFailure(error) {
  if (error?.rosterOutcome === 'uncertain') return { kind: 'uncertain', title: 'Outcome needs reconciliation', detail: 'The change may have been saved. Reload the map and assignment audit record before making another change.' }
  if (error?.rosterOutcome === 'review' || (error?.body?.uncertain !== true && error?.body?.ok === false && ((error.status === 409 && error.body.error === 'roster_changed') || (error.status === 428 && error.body.error === 'roster_precondition_required'))))
    return { kind: 'review', title: 'Review required', detail: 'The assignment map changed or its version is unavailable. Reload it and review the current assignments before creating a new proposal.' }
  const refused = error?.body?.uncertain !== true && ((error?.status >= 400 && error?.status < 500 && error?.body?.ok === false && error?.body?.error)
    || (error?.status === 401 && error?.body?.error === 'unauthorized') || error?.notSignedIn)
  if (refused) return { kind: 'refused', title: 'Not saved', detail: error?.body?.detail || error?.body?.reason || error?.message || 'The server refused the change.' }
  return { kind: 'uncertain', title: 'Outcome needs reconciliation', detail: 'The change may have been saved. Do not repeat it. Reload the map and its assignment audit record to reconcile the outcome.' }
}

// Used for BOTH set and remove. Never obtains a newer fingerprint during submit.
export async function writeRoster(request, path, body, audit) {
  const generation = getSessionGeneration()
  if (!globalThis.navigator?.locks?.request) throw Object.assign(Error('This browser cannot safely coordinate assignment writes. Use a browser with Web Locks support.'), { status: 428, rosterOutcome: 'review' })
  return globalThis.navigator.locks.request('sr-roster-write-v1', { mode: 'exclusive', ifAvailable: true }, lock => {
    if (generation !== getSessionGeneration()) throw Object.assign(Error('Your session changed. Reload the assignment map before saving.'), { status: 428, rosterOutcome: 'review' })
    if (!lock) throw Object.assign(Error('Another tab is saving an assignment. Wait for its outcome and reload the map.'), { status: 428, rosterOutcome: 'review' })
    return performRosterWrite(request, path, body, audit)
  })
}
async function performRosterWrite(request, path, body, audit) {
  if (!/^[a-f0-9]{64}$/.test(body?.expectedFingerprint || ''))
    throw Object.assign(Error('Read and review the assignment map before saving.'), { status: 428, rosterOutcome: 'review' })
  if (rosterPending()) throw Object.assign(Error('A prior assignment change needs reconciliation.'), { rosterOutcome: 'uncertain' })
  const pending = { id: globalThis.crypto.randomUUID(), state: 'pending', path, body: body.body,
    expectedFingerprint: body.expectedFingerprint, proposed: normalizeProposal(body), at: new Date().toISOString() }
  try { savePending(pending) } catch {
    throw Object.assign(Error('Browser storage is unavailable; no assignment write was attempted.'), { status: 428, rosterOutcome: 'review' })
  }
  let responseReceived = false
  try {
    const result = await request(path, { method: 'POST', body, audit })
    responseReceived = true
    const snap = rosterSnapshot(result)
    const trim = value => value == null ? '' : String(value).trim()
    const reps = [...new Set((Array.isArray(body.reps) ? body.reps : [body.reps]).map(trim).filter(Boolean))]
    const entry = result.entry
    const removing = path.endsWith('/remove')
    const coherent = removing ? !Object.hasOwn(snap.document?.bodies || {}, body.body)
      : JSON.stringify(entry) === JSON.stringify(snap.document?.bodies?.[body.body])
        && JSON.stringify(entry?.reps) === JSON.stringify(reps) && entry?.lead === (trim(body.lead) || reps[0])
        && entry?.scope === (trim(body.scope) || null) && entry?.reason === trim(body.reason) && entry?.authorizedBy === trim(body.authorizedBy)
    if (!coherent || result?.ok !== true || result.body !== body.body || (path.endsWith('/remove') ? typeof result.removed !== 'boolean' : !Array.isArray(result.entry?.reps))
      || (!(path.endsWith('/remove') && result.removed === false) && snap.fingerprint === body.expectedFingerprint)) throw Error('Incomplete assignment receipt')
    clearPending(pending.id)
    return result
  } catch (error) {
    // A malformed 2xx receipt can occur after COMMIT; never classify it as a 428 refusal.
    const outcome = responseReceived ? 'uncertain' : rosterFailure(error).kind
    if (outcome === 'uncertain') {
      if (rosterPending()?.id === pending.id) savePending({ ...pending, state: 'uncertain' })
      error.rosterOutcome = 'uncertain'
    } else clearPending(pending.id)
    throw error
  }
}

function normalizeProposal(body) {
  const trim = value => value == null ? '' : String(value).trim()
  const reps = [...new Set((Array.isArray(body.reps) ? body.reps : [body.reps]).map(trim).filter(Boolean))]
  return { reps, lead: trim(body.lead) || reps[0], scope: trim(body.scope) || null, reason: trim(body.reason), authorizedBy: trim(body.authorizedBy) }
}
export function matchingRosterReceipt(pending, audit) {
  if (!pending || pending.kind === 'setup' || pending.kind === 'recovery' || !audit?.ok || !Array.isArray(audit.entries)) return null
  return audit.entries.find(e => {
    if (e.field !== 'assignment_body_roster' || e.outcome !== 'applied' || e.recordId !== 'body:' + pending.body
      || e.detail?.body !== pending.body || e.detail?.route !== 'POST ' + pending.path) return false
    try {
      const before = rosterSnapshot({ ok: true, rosterSnapshot: e.detail.before })
      const after = rosterSnapshot({ ok: true, rosterSnapshot: e.detail.after })
      if (before.fingerprint !== pending.expectedFingerprint || after.fingerprint === before.fingerprint || !after.present) return false
      if (pending.path.endsWith('/remove')) return !Object.hasOwn(after.document.bodies, pending.body)
      const entry = after.document.bodies[pending.body]
      return !!entry && JSON.stringify(normalizeProposal(entry)) === JSON.stringify(pending.proposed)
    } catch { return false }
  }) || null
}
export function acknowledgeRosterReceipt(id, map, audit) {
  rosterSnapshot(map)
  const pending = rosterPending()
  if (pending?.id !== id || !matchingRosterReceipt(pending, audit)) throw Error('A matching durable assignment receipt is required; the prior outcome remains unresolved.')
  clearPending(id)
}
